import React from 'react';
import { MapPin, Calendar, Factory, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { companyInfo } from '../mock/data';

const PSCatering = () => {
  const psCateringInfo = companyInfo.subsidiaries.psCatering;

  const services = [
    "Institutional catering for healthcare facilities",
    "Corporate dining solutions",
    "Educational institution meal programs", 
    "Large-scale event catering",
    "Specialized non-halal menu development",
    "Custom meal planning and nutrition services"
  ];

  const clientSectors = [
    {
      title: "Healthcare Facilities",
      description: "Hospitals, nursing homes, and medical centers requiring specialized non-halal dietary options",
      icon: <Users className="h-8 w-8 text-red-500" />
    },
    {
      title: "Corporate Clients",
      description: "Office buildings, corporate cafeterias, and business dining facilities",
      icon: <Factory className="h-8 w-8 text-blue-500" />
    },
    {
      title: "Educational Institutions",
      description: "Schools, universities, and educational facilities with diverse dietary requirements",
      icon: <Users className="h-8 w-8 text-green-500" />
    }
  ];

  const capabilities = [
    "Large-scale non-halal food preparation",
    "Menu customization for institutional needs",
    "Nutritional analysis and menu planning",
    "Food safety and quality control",
    "Flexible delivery and service options",
    "Cost-effective meal solutions"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">PS Catering Pte Ltd</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Specialized non-halal food manufacturing and catering services for 
              institutional customers with diverse dietary requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Factory className="h-8 w-8 text-blue-500" />
                <span className="text-blue-600 font-semibold text-lg">Non-Halal Manufacturing Specialist</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {psCateringInfo.name}
              </h2>
              
              <div className="space-y-4 text-gray-600 mb-6">
                <p>
                  Our Non-Halal Kitchen upholds the same commitment to quality, safety, and consistency 
                  as our halal operations, catering specifically to clients requiring non-halal food 
                  production. With dedicated equipment and workflows, the kitchen ensures complete 
                  segregation from halal facilities while maintaining the highest standards of hygiene, 
                  preparation, and taste.
                </p>
                <p>
                  It serves both institutional and commercial non-halal clients, delivering reliable, 
                  high-quality food solutions.
                </p>
              </div>

              {/* Company Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <span className="font-medium text-gray-700">Established</span>
                    </div>
                    <p className="text-gray-900">{psCateringInfo.established}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Factory className="h-4 w-4 text-blue-500" />
                      <span className="font-medium text-gray-700">UEN</span>
                    </div>
                    <p className="text-gray-900">{psCateringInfo.uen}</p>
                  </CardContent>
                </Card>
              </div>

              <Button className="bg-blue-600 hover:bg-blue-700">
                <Link to="/contact" className="flex items-center">
                  Contact PS Catering
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1563468304224-1fc761a1cbb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="PS Catering Kitchen"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Facility Location */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Facility
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our dedicated non-halal kitchen facility is strategically located to serve 
              our institutional customers efficiently across Singapore.
            </p>
          </div>

          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Central Kitchen - Non-Halal</h3>
                  
                  <div className="flex items-start space-x-3 mb-4">
                    <MapPin className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Address</p>
                      <p className="text-gray-600">{psCateringInfo.address}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-700">Facility Features:</h4>
                    <div className="space-y-2">
                      {[
                        "Dedicated non-halal food preparation areas",
                        "Advanced cooking and food processing equipment", 
                        "Temperature-controlled storage facilities",
                        "Quality control and testing laboratory",
                        "Efficient packaging and distribution systems"
                      ].map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="grid grid-cols-1 gap-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_teng-corporate/artifacts/slpqtir7_Picture6.jpg"
                      alt="Professional food preparation in non-halal kitchen"
                      className="w-full h-48 object-cover rounded-lg shadow-lg"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_teng-corporate/artifacts/lxtb4sp3_Picture7.jpg"
                        alt="Quality non-halal dishes prepared fresh"
                        className="w-full h-40 object-cover rounded-lg shadow-lg"
                      />
                      <img 
                        src="https://customer-assets.emergentagent.com/job_teng-corporate/artifacts/swwn46b7_Picture8.jpg"
                        alt="Specialty non-halal food products"
                        className="w-full h-40 object-cover rounded-lg shadow-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Services & Capabilities */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Services */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Services</h2>
              <p className="text-gray-600 mb-8">
                We provide comprehensive non-halal food services tailored to meet the 
                specific requirements of institutional customers across various sectors.
              </p>
              
              <div className="space-y-3">
                {services.map((service, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Capabilities */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Capabilities</h2>
              <p className="text-gray-600 mb-8">
                Our experienced team and modern facilities enable us to deliver 
                consistent, high-quality non-halal food solutions at scale.
              </p>
              
              <div className="space-y-3">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Sectors */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our non-halal catering services cater to diverse institutional clients 
              across multiple sectors in Singapore.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {clientSectors.map((sector, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {sector.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{sector.title}</h3>
                  <p className="text-gray-600">{sector.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Non-Halal Catering Services?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Contact PS Catering to discuss your institutional food service requirements 
            and learn how we can provide customized non-halal solutions for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/contact" className="flex items-center">
                Contact PS Catering
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/businesses">View All Companies</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PSCatering;